import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useLive } from "@/contexts/LiveContext";
import { Eye, Radio, RefreshCw } from "lucide-react";

function timeAgo(dateStr) {
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

function FeedRow({ e, topic, fresh }) {
  const isA = e.side === "a";
  return (
    <motion.li
      layout
      initial={{ opacity: 0, x: -16, backgroundColor: isA ? "rgba(37,99,235,0.18)" : "rgba(240,68,68,0.18)" }}
      animate={{ opacity: 1, x: 0, backgroundColor: "rgba(0,0,0,0)" }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.45, backgroundColor: { duration: 2 } }}
      className="flex items-center gap-3 px-3 py-2 rounded-lg"
      data-testid={`feed-row-${e.event_id}`}
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${isA ? "bg-view-a" : "bg-view-b"} ${fresh ? "animate-ping" : ""}`} />
      <p className="flex-1 text-sm text-dark min-w-0 truncate">
        Someone {e.is_change ? <span className="inline-flex items-center gap-1 text-neutral"><RefreshCw className="w-3 h-3" /> switched to</span> : "picked"}{" "}
        <span className={`font-semibold ${isA ? "text-view-a" : "text-view-b"}`}>{isA ? topic.side_a_label : topic.side_b_label}</span>
      </p>
      <span className="text-[11px] text-neutral shrink-0 tabular-nums">{timeAgo(e.created_at)}</span>
    </motion.li>
  );
}

export default function DebateRoom({ topic }) {
  const { api } = useAuth();
  const { subscribe, send, connected } = useLive();
  const [events, setEvents] = useState([]);
  const [watching, setWatching] = useState(0);
  const [freshId, setFreshId] = useState(null);
  const [, tick] = useState(0);
  const topicId = topic.topic_id;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/topics/${topicId}/feed`);
      setEvents(data.events);
      setWatching(data.watching);
    } catch {}
  }, [api, topicId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 15000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!connected) return;
    send({ type: "join", topic_id: topicId });
    return () => send({ type: "leave" });
  }, [connected, send, topicId]);

  useEffect(() => subscribe((msg) => {
    if (msg.topic_id !== topicId) return;
    if (msg.type === "room_count") setWatching(msg.count);
    if (msg.type === "vote_update" && msg.event) {
      setEvents((prev) => prev.some((e) => e.event_id === msg.event.event_id) ? prev : [msg.event, ...prev].slice(0, 30));
      setFreshId(msg.event.event_id);
      setTimeout(() => setFreshId(null), 2500);
    }
  }), [subscribe, topicId]);

  const tickerItems = events.slice(0, 8);
  const loop = tickerItems.length ? [...tickerItems, ...tickerItems, ...tickerItems] : [];

  return (
    <div className="bg-white rounded-2xl shadow-card p-5 sm:p-6 mb-4" data-testid="debate-room">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-xl text-dark flex items-center gap-2">
          <Radio className="w-5 h-5 text-view-b" /> DEBATE ROOM
        </h2>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-dark bg-gray-100 px-2.5 py-1 rounded-full" data-testid="room-watching">
          <Eye className="w-3.5 h-3.5 text-success" />
          <AnimatePresence mode="popLayout">
            <motion.span key={watching} initial={{ y: 6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -6, opacity: 0 }} className="tabular-nums">{watching}</motion.span>
          </AnimatePresence>
          watching
        </span>
      </div>

      {loop.length > 0 && (
        <div className="ticker relative overflow-hidden rounded-lg bg-gray-50 mb-3" data-testid="room-ticker">
          <div className="ticker-track flex items-center gap-8 px-3 py-2 whitespace-nowrap" style={{ animationDuration: `${Math.max(14, tickerItems.length * 5)}s` }}>
            {loop.map((e, i) => (
              <span key={`${e.event_id}-${i}`} className="text-xs text-dark flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${e.side === "a" ? "bg-view-a" : "bg-view-b"}`} />
                Someone just {e.is_change ? "switched to" : "picked"}{" "}
                <b className={e.side === "a" ? "text-view-a" : "text-view-b"}>{e.side === "a" ? topic.side_a_label : topic.side_b_label}</b>
              </span>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-gray-50 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-gray-50 to-transparent" />
        </div>
      )}

      {events.length === 0 ? (
        <p className="text-sm text-neutral text-center py-6" data-testid="room-empty">No live votes yet. Be the first to move the needle.</p>
      ) : (
        <ul className="max-h-64 overflow-y-auto -mx-3 divide-y divide-gray-50" data-testid="room-feed">
          <AnimatePresence initial={false}>
            {events.map((e) => <FeedRow key={e.event_id} e={e} topic={topic} fresh={freshId === e.event_id} />)}
          </AnimatePresence>
        </ul>
      )}
      <p className="mt-2 text-[11px] text-neutral">Votes are shown anonymously.</p>
    </div>
  );
}
